import type { Firestore } from 'firebase/firestore';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

export type PortalSsoModuleId = 'crm' | 'erp' | 'finance' | 'hrms';

/**
 * CRM API hosts the SSO exchange (Firebase Admin creates custom tokens).
 * Local dev hits the Express server; production uses the deployed API host.
 */
export function getCrmApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1') {
      return 'http://localhost:5001/api/v1';
    }
  }
  // Production: Firebase Hosting site obis-crm-api → Cloud Run (custom domain in console)
  return 'https://api-crm.obis-edu.com/api/v1';
}

export async function exchangePortalSsoToken(params: {
  userId: string;
  ssoToken: string;
  module: PortalSsoModuleId;
}): Promise<{ customToken: string }> {
  const base = getCrmApiBaseUrl();
  const res = await fetch(`${base}/auth/sso-exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: params.userId,
      sso_token: params.ssoToken,
      module: params.module,
    }),
  });
  const data = (await res.json()) as { success?: boolean; customToken?: string; error?: string };
  if (!res.ok || !data.success || !data.customToken) {
    throw new Error(data.error || 'SSO exchange failed');
  }
  return { customToken: data.customToken };
}

/** Store a short-lived handoff secret; validated server-side and exchanged for a custom token. */
export async function issuePortalSsoToken(
  db: Firestore,
  uid: string,
  module: PortalSsoModuleId
): Promise<string> {
  const token = Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) =>
    b.toString(16).padStart(2, '0')
  ).join('');
  const expiresAt = new Date(Date.now() + 15 * 60 * 1000);
  await updateDoc(doc(db, 'users', uid), {
    [`ssoTokens.${module}`]: {
      token,
      createdAt: serverTimestamp(),
      expiresAt,
      systemId: module,
    },
  });
  return token;
}

export function appendPortalSsoToUrl(baseUrl: string, uid: string, token: string): string {
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}user_id=${encodeURIComponent(uid)}&sso_token=${encodeURIComponent(token)}`;
}
